import { computed, ref, type Ref } from 'vue';
import type { BranchMember } from '@/entities/Branch';
import { getMemberPositions } from '../lib/getMemberPositions';

export const MAX_MINISTRANTES = 5;
export const MIN_MINISTRANTES = 1;

export interface OnboardingMinistranteRow {
  id: number;
  responsibleMemberId: number | null;
  responsibleUserPositionId: number | null;
  trainingDate: Date | null;
}

export function useOnboardingMinistrantes(
  members: Ref<BranchMember[]>,
  initialRows?: OnboardingMinistranteRow[]
) {
  let nextRowId = 1;

  function createEmptyMinistranteRow(): OnboardingMinistranteRow {
    return { id: nextRowId++, responsibleMemberId: null, responsibleUserPositionId: null, trainingDate: null };
  }

  const rows = ref<OnboardingMinistranteRow[]>(
    initialRows && initialRows.length > 0
      ? initialRows.map((row) => ({ ...row, id: nextRowId++ }))
      : [createEmptyMinistranteRow()]
  );

  const canAddMore = computed(() => rows.value.length < MAX_MINISTRANTES);
  const canRemove = computed(() => rows.value.length > MIN_MINISTRANTES);

  const isComplete = computed(() =>
    rows.value.every(
      (row) =>
        row.responsibleMemberId !== null &&
        row.responsibleUserPositionId !== null &&
        row.trainingDate !== null
    )
  );

  function addRow(): void {
    if (rows.value.length < MAX_MINISTRANTES) {
      rows.value.push(createEmptyMinistranteRow());
    }
  }

  function removeRow(index: number): void {
    if (rows.value.length > MIN_MINISTRANTES) {
      rows.value.splice(index, 1);
    }
  }

  function setResponsibleMemberId(index: number, memberId: number | null): void {
    const row = rows.value[index];
    if (!row) return;
    row.responsibleMemberId = memberId;
    const positions = getMemberPositions(members.value, memberId);
    row.responsibleUserPositionId = positions.length === 1 ? positions[0]!.id : null;
  }

  function setResponsibleUserPositionId(index: number, positionId: number | null): void {
    const row = rows.value[index];
    if (row) row.responsibleUserPositionId = positionId;
  }

  function setTrainingDate(index: number, date: Date | null): void {
    const row = rows.value[index];
    if (row) row.trainingDate = date;
  }

  return {
    rows,
    canAddMore,
    canRemove,
    isComplete,
    addRow,
    removeRow,
    setResponsibleMemberId,
    setResponsibleUserPositionId,
    setTrainingDate,
  };
}
